import { ExportOutlined, InfoCircleFilled } from '@ant-design/icons'
import { DeviceStatus, type Home } from '@lmc/ts-common'
import {
  Button,
  Card,
  ConfirmationModal,
  EmptyState,
  PageContentWrapper,
  PageHeader,
  PermissionIcon
} from '@lmc/ui-kit'
import { Text } from '@lmc/ui-kit/src/general'
import { showToast } from 'common/utils'
import { useAbilityAccess } from 'components/AccessControl'
import useDevicesAccess from 'components/AccessControl/features/useDevicesAccess'
import useHasHomesList from 'components/AccessControl/useHasHomesList'
import { NAMESPACE } from 'locale'
import { dacConstants } from 'locale/deviceAccessControl'
import { generalConstants } from 'locale/general'
import { type FC, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useQueryClient } from 'react-query'
import { useSelector } from 'react-redux'
import { useNavigate, useParams } from 'react-router-dom'
import styled from 'styled-components'
import { useDeviceById, useUpdateDeviceById } from '../../hooks'
import DevicesManageTable from './DevicesManageTable'
import { HelpTextLink } from './HelpTextLink'
import InlineNameEdit from './InlineNameEdit'

const HELP_ENABLED =
  'https://support.logmycare.co.uk/en/articles/8010671-how-do-i-manage-devices-in-my-care-home'

const HELP_DISABLED =
  'https://support.logmycare.co.uk/en/articles/8010663-how-do-i-set-up-device-access-control'

export const DeviceManageScreen: FC = () => {
  const navigate = useNavigate()

  const params = useParams()

  const { id } = params

  const queryClient = useQueryClient()

  const hasHomesList = useHasHomesList()

  const hasDeviceAccessControl = useDevicesAccess()

  const hasRolePermission = useAbilityAccess([
    { i: 'update', this: 'MobileDevice' }
  ])

  const [selectedHomes, setSelectedHomes] = useState<string[]>([])

  const [multiApprove, setMultiApprove] = useState(false)

  const [isInlineEditing, setIsInlinEditing] = useState<boolean | undefined>(
    false
  )

  const { device, isLoading } = useDeviceById(id, hasRolePermission)

  const { updateDevice, isLoading: isLoadingUpdateDevice } =
    useUpdateDeviceById(() => {
      queryClient.invalidateQueries({ queryKey: [`device-${id}`] })
      setSelectedHomes([])
      setMultiApprove(false)
    })

  const {
    updateDevice: updateDeviceName,
    isLoading: isLoadingUpdateDeviceName,
    error: errorName,
    reset: resetName
  } = useUpdateDeviceById(() => {
    queryClient.invalidateQueries({ queryKey: [`device-${id}`] })
  })

  const { t } = useTranslation(NAMESPACE.DEVICE_ACCESS_CONTROL)

  const homeId = useSelector(
    (state: { global: { home: Home } }) => state.global.home
  )?.id

  if (!hasRolePermission) {
    return (
      <PageContentWrapper
        $gap={0}
        $paddingTop={24}
        $paddingX={24}
        $paddingBottom={24}
      >
        <PageHeader
          title={t(dacConstants.DEVICES)}
          moreInfoLink={HELP_DISABLED}
        />
        <EmptyState
          title={t(dacConstants.PSST)}
          description={t(dacConstants.NO_PERMISSIONS)}
          icon={<PermissionIcon />}
        />
      </PageContentWrapper>
    )
  }

  const handleMultiApprove = () => {
    setMultiApprove(true)
    const homes = selectedHomes.map((id) => ({
      home: id,
      status: DeviceStatus.approved
    }))

    updateDevice({ id, homes })
      .then(() =>
        showToast({
          type: 'success',
          title: t(dacConstants.ACCESS_APPROVED)
        })
      )
      .catch(() =>
        showToast({ type: 'error', title: t(dacConstants.UPDATE_FAILED) })
      )
  }

  const hasHomesSelected = !!selectedHomes.length

  const headerSubtitle = (
    <>
      {t(dacConstants.MANAGE_DEVICE_SUBTITLE)} {t(dacConstants.LEARN)}{' '}
      <HelpTextLink href={HELP_ENABLED} target="_blank" rel="noreferrer">
        {t(dacConstants.HOW_TO_MANAGE)}
        <ExportOutlined />
      </HelpTextLink>
    </>
  )

  return (
    <PageContentWrapper
      $gap={0}
      $paddingTop={24}
      $paddingX={24}
      $paddingBottom={24}
      data-testid="device-manage-screen"
    >
      <PageHeader
        title={t(dacConstants.MANAGE_DEVICE)}
        subtitle={headerSubtitle}
        onBack={() => navigate(-1)}
        testId="device-manage-header"
      />
      <FormWrapper data-testid="device-manage-form">
        <InlineNameEdit
          variant="title"
          editLabel={t(dacConstants.EDIT)}
          isLoading={isLoadingUpdateDeviceName}
          value={device?.name}
          label={t(dacConstants.DEVICE_NAME)}
          isInlineEditing={isInlineEditing}
          handleUpdateDevice={(name) => {
            updateDeviceName({ name, id })
          }}
          setIsInlinEditing={setIsInlinEditing}
          error={errorName as string}
          onReset={resetName}
          testId="device-manage-name-edit"
        />
        <DetailsRow>
          <DetailColumn data-testid="device-manage-model">
            <DetailLabel>{t(dacConstants.MODEL_NAME)}</DetailLabel>
            <Text>{device?.model || 'N/A'}</Text>
          </DetailColumn>
          <DetailColumn data-testid="device-manage-version">
            <DetailLabel>{t(dacConstants.APP_VERSION)}</DetailLabel>
            <Text>{device?.version || 'N/A'}</Text>
          </DetailColumn>
        </DetailsRow>
      </FormWrapper>
      {hasDeviceAccessControl && (
        <TableCard data-testid="device-manage-table-wrapper">
          {hasHomesList && (
            <BulkApproveBar data-testid="device-manage-bulk-bar">
              {hasHomesSelected ? (
                <>
                  <BulkText>
                    {`${selectedHomes.length} ${t(dacConstants.SELECTED)}`}
                  </BulkText>
                  <ConfirmationModal
                    localisation={{
                      confirmText: t(generalConstants.CONFIRM),
                      title: t(dacConstants.APPROVING_ACCESS),
                      cancelText: t(generalConstants.CANCEL)
                    }}
                    modalContent={
                      <ModalContentWrapper>
                        <Text>
                          {t(dacConstants.APPROVE_DEVICE_ALL_SELECTED_DESC)}
                        </Text>
                        <StyledTextDesc level={1}>
                          {t(
                            dacConstants.ARE_YOU_SURE_YOU_WANT_APPROVE_THIS_SITE_ALL
                          )}
                        </StyledTextDesc>
                      </ModalContentWrapper>
                    }
                    confirmButtonProps={{ variant: 'primary' }}
                  >
                    <Button
                      disabled={isInlineEditing}
                      onClick={handleMultiApprove}
                      isLoading={multiApprove && isLoadingUpdateDevice}
                      data-cy="btn-approve-selected"
                    >
                      {t(dacConstants.APPROVE_ACCESS)}
                    </Button>
                  </ConfirmationModal>
                </>
              ) : (
                <BulkInfo data-testid="device-manage-bulk-info">
                  <InfoIcon />
                  <BulkText level={1}>
                    {t(dacConstants.SELECT_MULTIPLE_SITES)}
                  </BulkText>
                </BulkInfo>
              )}
            </BulkApproveBar>
          )}
          <DevicesManageTable
            device={device}
            updateDevice={updateDevice}
            id={id}
            isLoading={isLoading}
            isLoadingUpdateDevice={isLoadingUpdateDevice}
            isInlineEditing={isInlineEditing}
            selectedHomes={selectedHomes}
            setSelectedHomes={setSelectedHomes}
            hasHomesList={hasHomesList}
            homeId={homeId}
            testId="device-manage-table"
          />
        </TableCard>
      )}
    </PageContentWrapper>
  )
}

// The bulk-approve bar and the sites table share one rounded card (design node
// 1:5434/1:5499): the bar is the card's first row, sitting flush above the
// table's grey header row. The card owns the outer radius, so the table is kept
// square inside it.
const TableCard = styled(Card)`
  margin-top: 24px;
  padding: 0;
  overflow: hidden;
  border-radius: 16px;

  .ant-table {
    border-radius: 0;
  }
`

const BulkApproveBar = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
  padding: 16px 24px;
`

const BulkInfo = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`

const BulkText = styled(Text)`
  color: ${(props) => props.theme.color.neutral.color4};
`

const InfoIcon = styled(InfoCircleFilled)`
  color: ${(props) => props.theme.color.status.primary};
`

const StyledTextDesc = styled(Text)`
  font-weight: 500;
`

const FormWrapper = styled.div`
  width: 100%;
  margin-top: 24px;
  padding: 24px;
  background-color: ${(props) => props.theme.color.neutral.color11};
  border-radius: 20px;
  display: flex;
  flex-direction: column;
  gap: 16px;
`

const DetailsRow = styled.div`
  display: flex;
  gap: 64px;
`

const DetailColumn = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
`

const DetailLabel = styled(Text)`
  color: ${(props) => props.theme.color.neutral.color4};
`

const ModalContentWrapper = styled.div`
  display: flex;
  flex-direction: column;
`

export default DeviceManageScreen
